/**
 * TopBarV4 — the shared PMS/portal top bar (desktop ≥900px chrome).
 *
 * SCRUM-299 / MENU-3 (epic SCRUM-296): fills the MENU-1 skeleton into the full,
 * parameterized top bar and moves it down from `widgets/pms-shell-v4/components`
 * to the shared entities tier (beside `sidebar-v4` + `MobileNavDrawer`), built
 * kit-ready so the real 2nd consumer (the owner shell) adopts it without a
 * rewrite — at which point it promotes to `@sparta/ui-kit` per ADR
 * `frontend-0004`. It stays admin-local until then.
 *
 * Because it now lives in `entities`, it may NOT import PMS-specific data
 * (entities ⇏ widgets, ADR `frontend-0001`). So portal data is INJECTED by the
 * host (`PmsShellV4`): `quickAddItems` (required), `searchResults`,
 * `unreadCount`; and what a selection DOES is handed back via callbacks
 * (`onAI`, `onSearchSelect`, `onQuickAdd`) — the component never routes itself
 * (the same host-owned-action seam as `SidebarV4.onSelect`). `accountChildren`
 * is the host-passed AccountMenu slot.
 *
 * Behavior (delta over the MENU-1 skeleton — account + hamburger unchanged):
 *  - **Center search → Athena.** A hand-rolled search field (brand icon-left +
 *    bare input + ⌘K chip). On focus + non-empty query → a live-results
 *    dropdown of injected `searchResults` (substring match on label + sub). A
 *    result row closes + clears the field, then calls `onSearchSelect(result)`
 *    — **rows are interactive ONLY when `onSearchSelect` is provided**;
 *    otherwise they render as non-interactive display rows (never a clickable
 *    row that merely clears). A bottom "Ask Athena: {q}" row + the right-cluster
 *    Athena icon hand off to `onAI` (Enter / row → `onAI(q)`; icon → `onAI("")`).
 *    Esc + outside-click close the dropdown. Real navigation lands in MENU-6.
 *  - **Quick-add disclosure.** A `+` trigger (`aria-expanded`) opens a labeled
 *    group of the injected `quickAddItems`; each fires `onQuickAdd(label)`
 *    (label casing is load-bearing). Opens/closes on click + outside-click +
 *    Esc. It is a disclosure of Tab-navigable action buttons, NOT a
 *    `role="menu"` — the menu role would overclaim the roving arrow-key
 *    contract the handoff a11y doc scopes out (Enter/Esc/outside-close only).
 *  - **Alerts bell** shows an unread dot from `unreadCount`. The notifications
 *    panel it opens is MENU-6 (SCRUM-302), so the control is **disabled** today
 *    (an honest "not wired yet", not a dead click); the dot is the live
 *    informational status.
 *  - **Help** is **inert** in MENU-3 (no handler in the handoff) — rendered
 *    disabled rather than a dead button; a real target lands later (SCRUM-302).
 *  - **Account cluster + compact hamburger** — carried from MENU-1 unchanged,
 *    including the account trigger's `aria-haspopup="dialog"` (the shared
 *    AccountMenu is a `role="region"` disclosure surface). No control here is a
 *    WAI-ARIA menu: the account trigger is `haspopup="dialog"`; quick-add is a
 *    plain disclosure (`aria-expanded` only).
 *
 * Token/lint compliance: colors via `var(--token)`; the 40px icon buttons use
 * the kit `IconBtn` (not hand-rolled); the search `<input>` + the bespoke
 * dropdown/menu rows + the avatar trigger composite carry an `eslint-disable`
 * + SCRUM-299 citation (no kit primitive matches those layouts). Dropdowns use
 * `--shadow-md` (the design's `--shadow-lg` is undefined and MENU-3 adds no new
 * tokens). Microcopy is verbatim from `COPY.md`.
 */
import * as React from 'react';
import { Avatar, Icon, IconBtn } from '@sparta/ui-kit';

import type { SearchResult, QuickAddItem } from './types.js';

export interface TopBarV4Props {
  /** Signed-in user (avatar + a11y); name optional per /auth/me contract. */
  readonly user: { id: string; email: string; name?: string };
  /** Compact (mobile) layout — show the hamburger, hide the search field. */
  readonly compact?: boolean;
  /** Compact-only: open the mobile nav drawer. */
  readonly onMenu?: () => void;
  /** Toggle the account menu (the host owns open state + Escape). */
  readonly onAccount: () => void;
  /** Whether the account menu is currently open (drives aria-expanded). */
  readonly accountOpen: boolean;
  /** The shared `AccountMenu`, rendered by the host when `accountOpen`. */
  readonly accountChildren?: React.ReactNode;
  /** Ref to the avatar trigger (excluded from the menu's outside-click set). */
  readonly accountTriggerRef?: React.RefObject<HTMLButtonElement>;
  /** Hand off an Athena query. `onAI("")` opens Athena with no prefilled query
      (the right-cluster icon); Enter / the Ask-Athena row pass the typed query.
      **Optional** — when absent, the search field + the Ask-Athena icon render
      DISABLED (an honest "not wired yet", never an enabled control that silently
      no-ops), the same contract as `onSearchSelect` and Alerts/Help. The host
      wires Athena in MENU-6 (SCRUM-302); until then it stays disabled. (Simon
      review — no active dead controls.) */
  readonly onAI?: (query: string) => void;
  /** Host-owned result action. The component clears + closes locally, then
      hands off — it never navigates itself (navigation is MENU-6). **Result
      rows are interactive ONLY when this is provided**; without it they render
      as non-interactive display rows. */
  readonly onSearchSelect?: (result: SearchResult) => void;
  /** Fired with a quick-add item's label (casing is load-bearing). **Optional**
      — when absent, the quick-add trigger renders DISABLED (an honest "not wired
      yet"), matching `onAI` + Alerts/Help. The host wires the create flows in
      MENU-6 (SCRUM-302); until then the trigger stays disabled. (Simon review —
      no active dead controls.) */
  readonly onQuickAdd?: (label: string) => void;
  /** Quick-add menu entries. REQUIRED — no PMS default (entities ⇏ widgets). */
  readonly quickAddItems: readonly QuickAddItem[];
  /** Live search results to filter + render. Default `[]` (empty-safe). */
  readonly searchResults?: readonly SearchResult[];
  /** Unread alerts count — drives the bell dot. Default `0` (no dot). */
  readonly unreadCount?: number;
}

/**
 * `true` when the user prefers reduced motion (read on mount + on change), so
 * the hover-background transitions can be dropped (ACCESSIBILITY.md). SSR-safe.
 *
 * TODO(SCRUM-302): extract to `shared/hooks/usePrefersReducedMotion` once
 * #302 lands — `SidebarV4` is the 2nd consumer (it carries its own copy), so
 * this is the promote-on-2nd-consumer trigger; extracting now would conflict
 * with #302's in-flight SidebarV4 rewrite/move.
 */
function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = React.useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (): void => setReduced(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export function TopBarV4({
  user,
  compact = false,
  onMenu,
  onAccount,
  accountOpen,
  accountChildren,
  accountTriggerRef,
  onAI,
  onSearchSelect,
  onQuickAdd,
  quickAddItems,
  searchResults = [],
  unreadCount = 0,
}: TopBarV4Props): React.ReactElement {
  const [q, setQ] = React.useState('');
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [quickAddOpen, setQuickAddOpen] = React.useState(false);
  const searchRef = React.useRef<HTMLDivElement>(null);
  const quickAddRef = React.useRef<HTMLDivElement>(null);
  const quickAddHeadingId = React.useId();
  const reducedMotion = usePrefersReducedMotion();

  // A control whose host handler is absent renders DISABLED — an honest "not
  // wired yet" (like Alerts/Help), never an enabled control that silently
  // no-ops. The host wires onAI/onQuickAdd in MENU-6 (SCRUM-302). (Simon review.)
  const aiWired = onAI !== undefined;
  const quickAddWired = onQuickAdd !== undefined;

  const query = q.trim();
  const matches =
    query.length > 0
      ? searchResults.filter((r) =>
          `${r.label} ${r.sub}`.toLowerCase().includes(query.toLowerCase()),
        )
      : [];
  // The dropdown shows whenever the field is focused with a non-empty query —
  // even when `matches` is empty, the Ask-Athena row keeps Athena reachable.
  const showDropdown = searchOpen && query.length > 0;

  // One listener closes whichever popover is open on Escape / outside click.
  // (The account menu owns its OWN Escape in the host — these are independent.)
  React.useEffect(() => {
    if (!searchOpen && !quickAddOpen) return undefined;
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (searchOpen && searchRef.current && !searchRef.current.contains(t)) {
        setSearchOpen(false);
      }
      if (
        quickAddOpen &&
        quickAddRef.current &&
        !quickAddRef.current.contains(t)
      ) {
        setQuickAddOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setQuickAddOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [searchOpen, quickAddOpen]);

  const askAthena = (text: string): void => {
    onAI?.(text);
    setQ('');
    setSearchOpen(false);
  };
  const pickResult = (result: SearchResult): void => {
    onSearchSelect?.(result);
    setQ('');
    setSearchOpen(false);
  };
  const pickQuickAdd = (label: string): void => {
    setQuickAddOpen(false);
    onQuickAdd?.(label);
  };

  const hoverMotion = reducedMotion
    ? ''
    : 'transition-[background] duration-[var(--dur-fast)]';

  return (
    <header className="h-14 shrink-0 flex items-center gap-4 px-5 bg-[var(--bg-surface)] border-b border-solid border-[var(--border-subtle)]">
      {/* Compact: hamburger opens the nav drawer (the rail is hidden). */}
      {compact ? (
        <IconBtn label="Open menu" onClick={onMenu}>
          <Icon name="menu" size={20} aria-hidden />
        </IconBtn>
      ) : null}

      {/* Center search → Athena. Hidden on compact (matches the shipped TopPill
          + the MENU-1 skeleton). */}
      {!compact ? (
        <div className="flex-1 min-w-0 flex justify-center">
          <div ref={searchRef} className="relative w-full max-w-lg">
            <div
              className={`flex items-center gap-2.5 h-11 min-h-touch pl-1.5 pr-3 bg-[var(--bg-sunken)] border border-solid rounded-[var(--radius-full)] focus-within:ring-2 focus-within:ring-brand-500 ${
                aiWired ? '' : 'opacity-60'
              } ${
                searchOpen
                  ? 'border-[var(--brand-500)]'
                  : 'border-[var(--border-subtle)]'
              }`}
            >
              <span
                aria-hidden="true"
                className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--brand-500)] text-[var(--fg-on-brand)] shrink-0"
              >
                <Icon name="search" size={14} aria-hidden />
              </span>
              {/* eslint-disable-next-line @sparta/no-hand-rolled-input -- bespoke top-bar search (brand icon-left + bare borderless input + ⌘K chip); the kit TextField needs a visible label + field chrome that don't fit this composition (SCRUM-299). */}
              <input
                type="search"
                value={q}
                disabled={!aiWired}
                onChange={(e) => {
                  setQ(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && query.length > 0) {
                    askAthena(q);
                    e.currentTarget.blur();
                  }
                }}
                aria-label="Search"
                placeholder="Search, or press Enter to ask Athena…"
                // h-11 + min-h-touch on the INPUT itself (not just the wrapper):
                // the wrapper's items-center would otherwise leave the actual
                // interactive target at its intrinsic ~20px height (Simon #371).
                className="flex-1 min-w-0 h-11 min-h-touch border-none outline-none bg-transparent text-[13px] text-[var(--fg-primary)] disabled:cursor-not-allowed"
              />
              <span
                aria-hidden="true"
                className="text-[11px] px-1.5 py-0.5 bg-[var(--bg-surface)] rounded-[var(--radius-sm)] border border-solid border-[var(--border-subtle)] text-[var(--fg-tertiary)] shrink-0"
              >
                ⌘K
              </span>
            </div>

            {showDropdown ? (
              <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-[var(--bg-surface)] border border-solid border-[var(--border-subtle)] rounded-[var(--radius-lg)] [box-shadow:var(--shadow-md)] overflow-hidden">
                {matches.map((r, i) =>
                  onSearchSelect !== undefined ? (
                    // eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke search-result row (icon + label/sub); no kit primitive matches (SCRUM-299).
                    <button
                      key={`${r.label}-${i}`}
                      type="button"
                      onClick={() => pickResult(r)}
                      className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 border-none bg-transparent cursor-pointer text-left hover:bg-[var(--bg-hover)] ${
                        i > 0
                          ? 'border-t border-solid border-[var(--border-subtle)]'
                          : ''
                      } ${hoverMotion}`}
                    >
                      <Icon
                        name={r.icon}
                        size={16}
                        aria-hidden
                        className="text-[var(--fg-tertiary)] shrink-0"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-semibold text-[var(--fg-primary)] whitespace-nowrap overflow-hidden text-ellipsis">
                          {r.label}
                        </span>
                        <span className="block text-xs text-[var(--fg-tertiary)] whitespace-nowrap overflow-hidden text-ellipsis">
                          {r.sub}
                        </span>
                      </span>
                    </button>
                  ) : (
                    <div
                      key={`${r.label}-${i}`}
                      className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 ${
                        i > 0
                          ? 'border-t border-solid border-[var(--border-subtle)]'
                          : ''
                      }`}
                    >
                      <Icon
                        name={r.icon}
                        size={16}
                        aria-hidden
                        className="text-[var(--fg-tertiary)] shrink-0"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-semibold text-[var(--fg-primary)] whitespace-nowrap overflow-hidden text-ellipsis">
                          {r.label}
                        </span>
                        <span className="block text-xs text-[var(--fg-tertiary)] whitespace-nowrap overflow-hidden text-ellipsis">
                          {r.sub}
                        </span>
                      </span>
                    </div>
                  ),
                )}
                {/* Ask Athena — always interactive (onAI is required). */}
                {/* eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke Ask-Athena row (accent surface + Enter hint); no kit primitive matches (SCRUM-299). */}
                <button
                  type="button"
                  onClick={() => askAthena(q)}
                  className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 border-t border-solid border-[var(--border-subtle)] bg-[var(--bg-active)] cursor-pointer text-left ${hoverMotion}`}
                >
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--brand-500)] text-[var(--fg-on-brand)] shrink-0"
                  >
                    <Icon name="sparkles" size={13} aria-hidden />
                  </span>
                  <span className="flex-1 min-w-0 text-[13px] font-semibold text-[var(--fg-primary)] whitespace-nowrap overflow-hidden text-ellipsis">
                    Ask Athena: “{q}”
                  </span>
                  <span
                    aria-hidden="true"
                    className="text-[11px] text-[var(--fg-tertiary)] shrink-0"
                  >
                    Enter ↵
                  </span>
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      {/* Right cluster */}
      <div className="flex items-center gap-1.5 shrink-0">
        {/* Ask Athena — opens Athena with no prefilled query. */}
        <IconBtn
          label="Ask Athena"
          title="Ask Athena"
          disabled={!aiWired}
          onClick={() => onAI?.('')}
          className="text-[var(--brand-600)]"
        >
          <Icon name="sparkles" size={19} aria-hidden />
        </IconBtn>

        {/* Quick add — a DISCLOSURE of action buttons (not a WAI-ARIA menu):
            `aria-expanded` on the trigger; the popover is a labeled group of
            Tab-navigable buttons. No `aria-haspopup="menu"` (that would
            overclaim a roving arrow-key contract scoped out of MENU-3). */}
        <div ref={quickAddRef} className="relative">
          <IconBtn
            label="Quick add"
            title="Quick add"
            disabled={!quickAddWired}
            aria-expanded={quickAddOpen}
            onClick={() => setQuickAddOpen((o) => !o)}
            className={quickAddOpen ? 'bg-[var(--bg-active)]' : undefined}
          >
            <Icon name="plus" size={19} aria-hidden />
          </IconBtn>
          {quickAddOpen ? (
            <div
              role="group"
              aria-labelledby={quickAddHeadingId}
              className="absolute right-0 top-full mt-2 z-50 w-56 bg-[var(--bg-surface)] border border-solid border-[var(--border-subtle)] rounded-[var(--radius-lg)] [box-shadow:var(--shadow-md)] overflow-hidden p-1.5"
            >
              <div
                id={quickAddHeadingId}
                className="text-[10px] font-bold tracking-wider uppercase text-[var(--fg-tertiary)] px-2.5 pt-2 pb-1.5"
              >
                Create new
              </div>
              {quickAddItems.map((item) => (
                // eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke quick-add action row (icon chip + label); no kit primitive matches (SCRUM-299).
                <button
                  key={item.label}
                  type="button"
                  onClick={() => pickQuickAdd(item.label)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[var(--radius-md)] border-none bg-transparent cursor-pointer text-sm text-[var(--fg-primary)] text-left hover:bg-[var(--bg-hover)] ${hoverMotion}`}
                >
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-7 h-7 rounded-[var(--radius-md)] bg-[var(--bg-sunken)] text-[var(--fg-secondary)] shrink-0"
                  >
                    <Icon name={item.icon} size={15} aria-hidden />
                  </span>
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* Alerts + Help — both inert placeholders in MENU-3 (disabled; wired in
            MENU-6 / SCRUM-302). Hidden on COMPACT so the right cluster fits
            narrow ≤360px mobile widths (Simon review P2, SCRUM-299): on compact
            the center search is already hidden, so Ask Athena + Quick add +
            Account remain reachable. They return on the expanded top bar; their
            mobile treatment gets revisited when the real alerts/help targets
            land. */}
        {!compact ? (
          <>
            {/* Alerts — shows live unread status; the dot reflects unreadCount. */}
            <IconBtn
              label={
                unreadCount > 0 ? `Alerts, ${unreadCount} unread` : 'Alerts'
              }
              disabled
            >
              <Icon name="bell" size={19} aria-hidden />
              {unreadCount > 0 ? (
                <span
                  aria-hidden="true"
                  className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-[var(--danger-500)] border-2 border-solid border-[var(--bg-surface)]"
                />
              ) : null}
            </IconBtn>

            {/* Help — inert in MENU-3 (no handler in the handoff). */}
            <IconBtn label="Help" disabled>
              <Icon name="info" size={19} aria-hidden />
            </IconBtn>
          </>
        ) : null}

        {/* Account — WORKING, carried from MENU-1 unchanged. Generic accessible
            name (no email-derived name); the host renders the shared AccountMenu
            when open. The trigger stays haspopup="dialog" (the AccountMenu is a
            role="region" disclosure, not a WAI-ARIA menu). */}
        <div className="relative">
          {/* eslint-disable-next-line @sparta/no-hand-rolled-button -- avatar trigger composite (avatar + chevron); mirrors widgets/admin-shell/components/AdminTopPill.tsx + the MENU-1 skeleton. */}
          <button
            ref={accountTriggerRef}
            type="button"
            aria-label="Open account menu"
            aria-haspopup="dialog"
            aria-expanded={accountOpen}
            onClick={onAccount}
            className={`flex items-center gap-1.5 h-11 min-h-touch pl-1 pr-2 rounded-[var(--radius-full)] border-none cursor-pointer text-[var(--fg-primary)] bg-transparent hover:bg-[var(--bg-hover)] ${hoverMotion}`}
          >
            <Avatar name={user.name} size={32} />
            <Icon name="chevDown" size={14} aria-hidden />
          </button>
          {accountOpen ? accountChildren : null}
        </div>
      </div>
    </header>
  );
}
